import React from 'react';
import { Box, Heading } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format, parseISO } from 'date-fns';
import type { TimeseriesPoint } from '../../types/link';
import { CHART_FONT, AXIS_LABEL_STYLE, GRID_COLOR } from './chartTheme';

import { PRIMARY_BG } from '../../../../../theme/brand';
/** Clicks & installs over time — Highcharts area-spline, matching the xNotify
 *  dashboard trend charts (dotted gridlines, Inter font, HTML tooltip, legend).
 *  No per-point labels — same as the app's line charts (too dense to label). */
export const ClicksOverTimeChart: React.FC<{ data: TimeseriesPoint[]; showInstalls?: boolean }> = ({
  data,
  showInstalls = true,
}) => {
  const fmtAxis = (iso: string) => {
    try {
      return format(parseISO(iso), 'dd MMM');
    } catch {
      return iso;
    }
  };

  const options: Highcharts.Options = {
    credits: { enabled: false },
    title: { text: undefined },
    chart: {
      type: 'areaspline',
      height: 300, // explicit — HighchartsReact mis-measures the flex container otherwise (drops x-axis + legend)
      backgroundColor: 'transparent',
      style: { fontFamily: CHART_FONT },
      spacing: [10, 8, 4, 6],
    },
    colors: [PRIMARY_BG, '#10b981'],
    xAxis: {
      categories: data.map((d) => fmtAxis(d.date)),
      lineWidth: 0,
      tickWidth: 0,
      labels: { style: { ...AXIS_LABEL_STYLE } },
    },
    yAxis: {
      title: { text: null },
      allowDecimals: false,
      gridLineDashStyle: 'Dot',
      gridLineColor: GRID_COLOR,
      labels: { style: { ...AXIS_LABEL_STYLE } },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: { fontSize: '12px', fontWeight: '500', color: '#4b5563' },
    },
    tooltip: { shared: true, useHTML: true },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.14,
        lineWidth: 2,
        marker: { enabled: false, symbol: 'circle' },
      },
    },
    // SM_004 — Installs series only for links that attribute installs; otherwise
    // the chart is Clicks-only (no misleading flat Installs line / legend item).
    series: [
      { type: 'areaspline', name: 'Clicks', data: data.map((d) => d.clicks), color: PRIMARY_BG },
      ...(showInstalls
        ? [{ type: 'areaspline' as const, name: 'Installs', data: data.map((d) => d.installs), color: '#10b981' }]
        : []),
    ],
  };

  return (
    <Box bg="white" borderWidth="1px" rounded="lg" p={4} shadow="xs">
      <Heading size="xs" color="gray.600" mb={3}>
        {showInstalls ? 'Clicks & installs over time' : 'Clicks over time'}
      </Heading>
      <Box h="300px">
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: '100%', width: '100%' } }}
        />
      </Box>
    </Box>
  );
};
